<!--

		A vimeo video that is streamed from our S3 backups instead of directly from Vimeo.
		Can be used as a fallback when Vimeo is down.
		Supports most of the same features as `VimeoPlayer.vue`, but renders in a native <video> element.

		Note that while this player is mostly reactive, you should not assume that
		all props can be updated in all situations. In particular, you should make
		sure that it does not get recylced by different displays, e.g., by adding a
		:key to it in the parent component.

-->
<template>
	<div class="mia-VimeoS3FallbackPlayer">
		<video
			ref="video"
			controls
			v-bind="autoPlay ? { autoplay: true } : {}"
			crossorigin="anonymous"
			:class="isVerticalVideo ? 'mia-vertical' : 'mia-horizontal'"
		>
			<source v-if="videoSrc" :src="videoSrc">
		</video>
	</div>
</template>

<script lang="ts">
import axios from "axios";
import { Component, Prop, Vue, Watch, toNative } from "vue-facing-decorator";
@Component
export class VimeoS3FallbackPlayer extends Vue {
	@Prop({ required: true }) protected vimeoId!: string;
	@Prop({ required: false, default: false }) autoPlay!: boolean;
	@Prop({ required: false, default: null }) protected volume!: number | null;
	// Currently we ignore this prop. We would have to load the subtitles from S3 for this to work.
	@Prop({ required: false, default: null }) protected textTrack!: string | null;
	@Prop({ required: false, default: null }) protected time!: number | null;
	@Prop({ required: true, type: String }) protected fallbackS3BucketEndpoint!: string;
	@Prop({ required: false, default: false }) protected isPlaying!: boolean;
	@Prop({ required: false, default: false }) isVerticalVideo!: boolean;

	videoSrc: string | null = null;

	// The time stamp we reported most recently in a update:time event.
	private timeupdate: number | null = null;

	private duration = 0;

	private get video(): HTMLVideoElement {
		return this.$refs.video as HTMLVideoElement;
	}

	async mounted(): Promise<void> {
		await this.onVimeoIdChange(this.vimeoId);

		this.onTimeChange();
		this.onVolumeChange();

		this.video.addEventListener("timeupdate", () => {
			this.timeupdate = this.video.currentTime;
			this.duration = this.video.duration;
			this.$emit("update:time", this.timeupdate, this.duration);
		});

		this.video.addEventListener("ended", () => this.$emit("ended"));
		this.video.addEventListener("volumechange", () => this.$emit("update:volume", this.video.volume));
		this.video.addEventListener("pause", () => this.$emit("pause"));
		this.video.addEventListener("play", () => this.$emit("play"));
	}

	private getS3VideoUrl(vimeoId: string, fileType: string): string {
		return `${this.fallbackS3BucketEndpoint}${vimeoId}/video.${fileType}`;
	}

	/**
	 * We either have a mp4 or webm video file in our backup.
	 * We check the existence of the file before trying to load it as a video by doing a simple HEAD
	 * request to S3, first mp4 because we know that those are more common.
	 */
	@Watch("vimeoId")
	protected async onVimeoIdChange(value: string): Promise<void> {
		if (this.videoSrc != null) {
			// The HTML video is not fully reactive. We cannot switch the source
			// while the video is playing. (Also we would have to rewind the video to
			// the correct place, reestablish subtitles, ...)
			// Instead, you probably want to recreate this component with a :key that
			// depends on the vimeo id.
			throw Error("VimeoS3FallbackPlayer cannot reactively update when the vimeo id changes yet.");
		}

		for (const type of ["mp4", "webm"]) {
			const url = this.getS3VideoUrl(value, type);

			if (await this.existsVideoFile(url)) {
				if (value !== this.vimeoId) {
					// The vimeo Id has changed before we determined the source.
					// We ignore this obsolete request.
					return;
				}

				this.videoSrc = url;

				return;
			}
		}

		throw Error(`Could not fallback to S3 for video, video file for ${value} seems to be missing.`);
	}

	private async existsVideoFile(url: string): Promise<boolean> {
		const response = await axios.head(url, { validateStatus: null });
		return response.status === 200;
	}

	@Watch("time")
	private onTimeChange() {
		if (this.time == null) {
			return;
		}
		if (this.time !== this.timeupdate) {
			this.video.currentTime = this.time;
			this.timeupdate = this.time;
		}
	}

	@Watch("volume")
	private onVolumeChange() {
		if (this.volume == null) {
			return;
		}
		this.video.volume = this.volume;
	}

	@Watch("isPlaying")
	private async onIsPlayingChange() {
		if (this.isPlaying) {
			await this.video.play();
			return;
		}
		this.video.pause();
	}
}

export default toNative(VimeoS3FallbackPlayer);
</script>
<style lang="scss">
.mia-VimeoS3FallbackPlayer {
	width: 100%;
	video {
		border: none;
		width: 100%;

		&.mia-vertical {
			aspect-ratio: 9 / 16;
		}

		&.mia-horizontal {
			aspect-ratio: 16 / 9;
		}
	}
}
</style>
